import { useCallback, useEffect, useState } from "react";
import { lerQuadro, type Quadro, type QuadroCaderno } from "../lib/correcaoCfc";
import {
  COR_DA_CELULA,
  LINHAS_POR_COLUNA,
  celulaDoQuadro,
  corDoCaderno,
  numeroNoQuadro,
} from "../lib/quadroCfc";

// /correcao-cfc — PÚBLICA (000384): o quadro da correção ao vivo do Exame de Suficiência, que era o
// `quadro-correcao-cfc` (Flutter, no Fly.io). O quadro de lá continua no ar até a virada, que é decisão
// do Enzo (`docs/runbooks/correcao-cfc.md`).
//
// O desenho é o de lá, porque o quadro vai para a TRANSMISSÃO: o fundo navy inteiro, os quatro
// cadernos à direita (a esquerda fica livre para quem apresenta), cada caderno na cor dele e com o
// título de lado, e as questões em colunas de 10, de cima para baixo. Cor FIXA, e não token: o quadro
// é papel, não troca com o tema de quem o abre.
//
// Lá o quadro ouvia o banco em tempo real. Aqui ele relê a cada 5 segundos, e só com a aba visível: a
// função lê 200 linhas pequenas, e o tempo real exigiria abrir as tabelas à Data API, que é o que o
// `revoke all` delas impede. Cinco segundos é o atraso de quem corrige ao vivo, dito na tela.
//
// Quem decide o que aparece é o banco (`correcao_quadro`): a alternativa que a equipe ainda não liberou
// não chega ao navegador.

const INTERVALO_MS = 5000;

export default function QuadroCfc() {
  const [quadro, setQuadro] = useState<Quadro | null>(null);
  const [semConexao, setSemConexao] = useState(false);
  const [lido, setLido] = useState(false);

  const ler = useCallback(async () => {
    const q = await lerQuadro();
    setLido(true);
    if (!q) {
      // A falha de uma leitura não apaga o quadro que já está na tela: ela só é dita.
      setSemConexao(true);
      return;
    }
    setSemConexao(false);
    setQuadro(q);
  }, []);

  useEffect(() => {
    document.title = "Correção CFC ao vivo — Contabilidade Facilitada";
    void ler();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void ler();
    }, INTERVALO_MS);
    const aoVoltar = () => {
      if (document.visibilityState === "visible") void ler();
    };
    document.addEventListener("visibilitychange", aoVoltar);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [ler]);

  return (
    <main className="relative min-h-dvh bg-[#102850] text-white" data-quadro-cfc>
      {!lido && <p className="p-6 text-sm text-[#aab4c8]">Abrindo o quadro…</p>}
      {lido && !quadro && (
        <p className="p-6 text-sm text-[#aab4c8]" data-quadro-estado="sem-conexao">
          Não foi possível abrir o quadro agora. Ele tenta de novo sozinho em alguns segundos.
        </p>
      )}
      {quadro && !quadro.ok && (
        <p className="p-6 text-base text-[#aab4c8]" data-quadro-estado="sem-edicao">
          O quadro da correção ainda não está no ar.
        </p>
      )}
      {quadro?.ok && <Painel quadro={quadro} />}
      {semConexao && quadro && (
        <p className="absolute right-4 bottom-2 text-xs text-[#aab4c8]" role="status" data-quadro-estado="tentando">
          Sem conexão agora — o quadro mostra a última leitura e tenta de novo sozinho.
        </p>
      )}
    </main>
  );
}

function Painel({ quadro }: { quadro: Extract<Quadro, { ok: true }> }) {
  return (
    <>
      <h1 className="sr-only">Correção do Exame de Suficiência {quadro.edicao}, ao vivo</h1>
      {quadro.mostrar_download && (
        <div className="flex flex-col gap-2 p-2 lg:absolute lg:top-0 lg:left-0" data-baixar>
          {quadro.cadernos.map((c) => (
            <button
              key={c.ordem}
              type="button"
              className="min-h-10 rounded-full bg-[#cc9933] px-4 text-sm font-bold text-[#102850] hover:brightness-110"
              onClick={() => baixarPng(c, quadro.edicao)}
              data-baixar-caderno={c.ordem}
            >
              Baixar — {c.titulo}
            </button>
          ))}
        </div>
      )}
      <div
        className="ml-auto flex min-h-dvh w-full flex-col gap-3 p-3 sm:p-4 lg:w-[58%]"
        data-quadro-painel
        data-edicao={quadro.edicao}
      >
        <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-2">
          {quadro.cadernos.map((c) => (
            <Caderno key={c.ordem} c={c} />
          ))}
        </div>
        <Legenda />
      </div>
    </>
  );
}

function Caderno({ c }: { c: QuadroCaderno }) {
  const cor = corDoCaderno(c.ordem);
  const colunas = Math.max(1, Math.ceil(c.questoes.length / LINHAS_POR_COLUNA));
  const tituloAEsquerda = c.ordem % 2 === 1; // como lá: Tipo 1 e 3 à esquerda, 2 e 4 à direita
  const titulo = (
    <div
      className={`flex w-9 shrink-0 items-center justify-center sm:w-11 ${tituloAEsquerda ? "rounded-l-[20px]" : "rounded-r-[20px]"}`}
      style={{ background: cor.fundo }}
      aria-hidden="true"
    >
      <span className="text-lg font-bold tracking-wide text-white uppercase [writing-mode:vertical-rl] rotate-180 sm:text-2xl">
        {c.titulo}
      </span>
    </div>
  );
  return (
    <section className="flex min-h-80 items-stretch" aria-label={c.titulo} data-caderno={c.ordem}>
      {tituloAEsquerda && titulo}
      <ol
        // O bloco não arredonda do lado do título: arredondado, ele deixava um recorte entre os dois.
        className={`grid flex-1 grid-flow-col gap-x-1 gap-y-1 p-3 ${tituloAEsquerda ? "rounded-r-[23px]" : "rounded-l-[23px]"}`}
        style={{
          background: cor.fundo,
          gridTemplateRows: `repeat(${LINHAS_POR_COLUNA}, minmax(0, 1fr))`,
          gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))`,
        }}
      >
        {c.questoes.map((q) => {
          const cel = celulaDoQuadro(q.alternativa);
          return (
            <li key={q.numero} className="flex min-w-0 items-center gap-1" data-questao={q.numero} data-alternativa={q.alternativa ?? ""}>
              <span className="w-6 shrink-0 text-right text-xs font-bold text-white tabular-nums sm:text-sm">{numeroNoQuadro(q.numero)}</span>
              <span
                // "D/R" é três vezes mais largo que uma letra: na fonte da letra, ele passava da célula
                // e cobria o número da coluna ao lado (a foto de 03/11).
                className={`flex h-full min-h-7 min-w-0 flex-1 items-center justify-center rounded-lg border font-bold leading-none ${
                  cel.texto.length > 1 ? "text-xs sm:text-sm" : "text-lg sm:text-2xl"
                }`}
                style={{
                  background: cel.alerta ? COR_DA_CELULA.fundoAlerta : COR_DA_CELULA.fundo,
                  color: cel.alerta ? COR_DA_CELULA.tintaAlerta : COR_DA_CELULA.tinta,
                  borderColor: cor.borda,
                }}
                data-celula
                data-celula-alerta={cel.alerta ? "" : undefined}
              >
                {cel.texto}
                <span className="sr-only">{cel.texto ? "" : "ainda não liberada"}</span>
              </span>
            </li>
          );
        })}
      </ol>
      {!tituloAEsquerda && titulo}
    </section>
  );
}

function Legenda() {
  const item = (texto: string, rotulo: string) => (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-flex h-7 min-w-9 items-center justify-center rounded-md border px-1 text-sm font-bold"
        style={{ background: COR_DA_CELULA.fundoAlerta, color: COR_DA_CELULA.tintaAlerta, borderColor: COR_DA_CELULA.tintaAlerta }}
      >
        {texto}
      </span>
      <span className="text-sm font-bold text-white">{rotulo}</span>
    </span>
  );
  return (
    <p className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2" data-legenda>
      {item("R", "RECURSO")}
      {item("A/R", "ALTERNATIVA COM RECURSO")}
      {item("X", "ANULADA")}
    </p>
  );
}

/**
 * O PNG de um caderno, desenhado num canvas com as MESMAS regras da tela (`celulaDoQuadro` e as cores
 * de `quadroCfc.ts`). Desenhar, e não fotografar o DOM: a foto dependeria do tamanho da janela de quem
 * clica, e o arquivo sai sempre igual.
 */
function baixarPng(c: QuadroCaderno, edicao: string) {
  const escala = 3;
  const colunas = Math.max(1, Math.ceil(c.questoes.length / LINHAS_POR_COLUNA));
  const larguraTitulo = 44;
  const larguraColuna = 120;
  const alturaLinha = 52;
  const margem = 16;
  const largura = larguraTitulo + margem * 2 + colunas * larguraColuna;
  const altura = margem * 2 + LINHAS_POR_COLUNA * alturaLinha;
  const canvas = document.createElement("canvas");
  canvas.width = largura * escala;
  canvas.height = altura * escala;
  const g = canvas.getContext("2d");
  if (!g) return;
  g.scale(escala, escala);
  const cor = corDoCaderno(c.ordem);
  g.fillStyle = cor.fundo;
  g.beginPath();
  g.roundRect(0, 0, largura, altura, 23);
  g.fill();
  // O título, de lado.
  g.save();
  g.translate(larguraTitulo / 2 + 4, altura / 2);
  g.rotate(-Math.PI / 2);
  g.fillStyle = "#ffffff";
  g.font = "bold 22px Inter, system-ui, sans-serif";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(c.titulo.toUpperCase(), 0, 0);
  g.restore();
  c.questoes.forEach((q, i) => {
    const col = Math.floor(i / LINHAS_POR_COLUNA);
    const lin = i % LINHAS_POR_COLUNA;
    const x = larguraTitulo + margem + col * larguraColuna;
    const y = margem + lin * alturaLinha;
    g.fillStyle = "#ffffff";
    g.font = "bold 15px Inter, system-ui, sans-serif";
    g.textAlign = "right";
    g.textBaseline = "middle";
    g.fillText(numeroNoQuadro(q.numero), x + 26, y + alturaLinha / 2);
    const cel = celulaDoQuadro(q.alternativa);
    g.fillStyle = cel.alerta ? COR_DA_CELULA.fundoAlerta : COR_DA_CELULA.fundo;
    g.strokeStyle = cor.borda;
    g.lineWidth = 1;
    g.beginPath();
    g.roundRect(x + 32, y + 5, larguraColuna - 42, alturaLinha - 10, 8);
    g.fill();
    g.stroke();
    if (cel.texto) {
      g.fillStyle = cel.alerta ? COR_DA_CELULA.tintaAlerta : COR_DA_CELULA.tinta;
      g.font = "bold 26px Inter, system-ui, sans-serif";
      g.textAlign = "center";
      g.fillText(cel.texto, x + 32 + (larguraColuna - 42) / 2, y + alturaLinha / 2 + 1);
    }
  });
  const a = document.createElement("a");
  a.href = canvas.toDataURL("image/png");
  a.download = `quadro_${edicao}_${c.titulo.toLowerCase().replace(/\s+/g, "_")}.png`;
  a.click();
}
